/**
 * The `Intl` formatters of FR-015-131 and `research.md` W-22. Two timezones exist and every
 * function names which it is in: the workspace timezone for a calendar date on an item, the
 * browser timezone for a wall-clock timestamp. Relative times are computed against the
 * server clock through the offset `@baton/core` captures from the `Date` header.
 */

/** A calendar date as the API writes it, `YYYY-MM-DD`. */
export type IsoDate = `${number}-${number}-${number}`;

export interface FormatterOptions {
  readonly locale: string;
  /** The workspace timezone from `GET /workspaces/{ws}`, an IANA name. */
  readonly workspaceTimeZone: string;
  /** The browser timezone; the host supplies it so the module has no DOM or `Intl` default dependency. */
  readonly browserTimeZone: string;
}

export interface Formatters {
  /** A calendar date on an item, in the workspace timezone: "20 Sept 2026". */
  itemDate(date: IsoDate | Date, style?: "short" | "medium" | "long"): string;
  /** A calendar date as a page's title names its day, the weekday, the day and the month in full: "Tuesday 21 October" (Home's title, 2026-10-21). */
  fullDay(date: IsoDate): string;
  /** A wall-clock instant, in the browser timezone: "20 Sept 2026, 14:05". */
  timestamp(instant: Date | string, style?: "short" | "medium" | "long"): string;
  /** The time of day of an instant, in the browser timezone. */
  time(instant: Date | string): string;
  /** The day an instant falls on, in the browser timezone, by `when`'s rule for a day: "Today", "Yesterday", "Tomorrow", the weekday within six days either side, "Oct 1" in `now`'s year and "Oct 1, 2025" otherwise; for a meeting's day beside its span of hours. */
  day(instant: Date | string, now: Date): string;
  /** From one instant to another, in the browser timezone: "9:00 – 9:30 AM" within one day, each side's date written when they are on different days. */
  timeRange(start: Date | string, end: Date | string): string;
  /**
   * When an instant happened or happens, by the one rule every screen follows (2026-10-03, the owner's
   * approval of the product review: "one rule: relative within a week, "Oct 3" within the year, the year
   * only otherwise"), against `now` (the server-corrected clock), in the browser timezone: "Now" within a
   * minute, "14m" and "2h" earlier the same day ("in 14m", "in 2h" later the same day), "Yesterday" and
   * "Tomorrow", the weekday ("Tuesday") within six days either side, "Oct 3" in `now`'s year and
   * "Oct 3, 2025" otherwise. `time` adds the time of day where it matters, an email's or a meeting's:
   * "Yesterday 6:54 PM", "Oct 3 at 6:54 PM", and the same day's time of day itself once it is an hour or
   * more away ("6:54 PM").
   */
  when(instant: Date | string, now: Date, options?: { readonly time?: boolean }): string;
  /**
   * A day by the same rule's year part and with no relative word (2026-10-03), for a calendar date (a
   * due date, a sprint's span), which reads by its day, and for an instant written into a sentence
   * ("Deleted for good on Oct 3"): "Oct 3" in `now`'s year and "Oct 3, 2025" otherwise. A calendar date
   * (`YYYY-MM-DD`) is the day as written, compared with the workspace's today; an instant is the day it
   * falls on in the browser timezone, and `time` adds its time of day ("Oct 3 at 6:54 PM"). `now` is the
   * server-corrected clock, or the workspace's today where a host holds that instead (a date filter).
   */
  date(value: IsoDate | Date, now: Date | IsoDate, options?: { readonly time?: boolean }): string;
  number(value: number, options?: Intl.NumberFormatOptions): string;
  percent(fraction: number): string;
  list(items: readonly string[], kind?: "conjunction" | "disjunction"): string;
  /** The `YYYY-MM-DD` of an instant in the workspace timezone, which is what "today" means. */
  workspaceDateOf(instant: Date): IsoDate;
  /** The `YYYY-MM-DD` of an instant in the browser timezone, the day the person's own clock gives it: the day `when` and `day` count from (2026-10-13, the inbox's day groups). */
  browserDateOf(instant: Date | string): IsoDate;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
/** The days either side of today that the weekday alone names (§ `when`). */
const WEEKDAY_SPAN = 6;

/** The text with its first letter in upper case, for a relative word standing alone ("Yesterday", "Now"). */
function sentenceCase(text: string, locale: string): string {
  return text.length === 0 ? text : text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/** A `YYYY-MM-DD` is a calendar date with no instant behind it: it is parsed as noon UTC
 * and formatted in UTC, so no timezone can shift it across a day boundary. An instant, by
 * contrast, is formatted in the workspace timezone to find which calendar day it falls on. */
function isoDateToInstant(date: IsoDate): Date {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12));
}

/**
 * One `Intl` formatter per locale, zone and options, made on first use and kept for the formatters' life. Constructing one
 * costs far more than formatting with it, and a board drag formatted a column's counts and a card's dates on every frame
 * that moved a card, each time through a new instance (2026-09-29, SC-015-006's drag frames).
 */
function cached<T>(make: (key: string) => T): (key: string) => T {
  const made = new Map<string, T>();
  return (key) => {
    const found = made.get(key);
    if (found !== undefined) return found;
    const fresh = make(key);
    made.set(key, fresh);
    return fresh;
  };
}

export function createFormatters(options: FormatterOptions): Formatters {
  const { locale, workspaceTimeZone, browserTimeZone } = options;
  const dateStyle = (style: "short" | "medium" | "long") => ({ dateStyle: style } as const);
  const workspaceParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: workspaceTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  type Style = "short" | "medium" | "long";
  const itemDateIn = cached((key) => {
    const [style, zone] = key.split(" ") as [Style, string];
    return new Intl.DateTimeFormat(locale, { ...dateStyle(style), timeZone: zone });
  });
  const fullDayFormat = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
  const timestampIn = cached((style) => new Intl.DateTimeFormat(locale, { ...dateStyle(style as Style), timeStyle: "short", timeZone: browserTimeZone }));
  const timeOfDay = new Intl.DateTimeFormat(locale, { timeStyle: "short", timeZone: browserTimeZone });
  const browserParts = new Intl.DateTimeFormat("en-CA", { timeZone: browserTimeZone, year: "numeric", month: "2-digit", day: "2-digit" });
  const acrossDays = new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short", timeZone: browserTimeZone });
  const relativeWords = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const relativeNarrow = new Intl.RelativeTimeFormat(locale, { style: "narrow" });
  const unitNarrow = cached((unit) => new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: "narrow" }));
  const weekdayIn = cached((withTime) => new Intl.DateTimeFormat(locale, { weekday: "long", ...(withTime === "time" ? { hour: "numeric", minute: "2-digit" } : {}), timeZone: browserTimeZone }));
  const monthDayIn = cached((key) => {
    const [year, withTime, zone] = key.split(" ");
    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", ...(year === "year" ? { year: "numeric" } : {}), ...(withTime === "time" ? { hour: "numeric", minute: "2-digit" } : {}), timeZone: zone ?? browserTimeZone });
  });
  const sameBrowserYear = (date: Date, now: Date) => browserParts.format(date).slice(0, 4) === browserParts.format(now).slice(0, 4);
  /** The day `days` from `now`'s in words: today, yesterday or tomorrow, the weekday within six days, else the month and day with the year outside `now`'s; `time` adds the time of day. */
  const dayFrom = (date: Date, now: Date, days: number, time: boolean): string => {
    if (Math.abs(days) <= 1) {
      const word = sentenceCase(relativeWords.format(days, "day"), locale);
      // The locale's own pattern for a weekday and a time, with the weekday's place taken by the word.
      return time ? weekdayIn("time").formatToParts(date).map((part) => (part.type === "weekday" ? word : part.value)).join("") : word;
    }
    if (Math.abs(days) <= WEEKDAY_SPAN) return weekdayIn(time ? "time" : "").format(date);
    return monthDayIn(`${sameBrowserYear(date, now) ? "" : "year"} ${time ? "time" : ""} ${browserTimeZone}`).format(date);
  };
  /** The year of `now`, the workspace's today's where a calendar day is compared with it. */
  const workspaceYear = (now: Date | IsoDate) => (now instanceof Date ? workspaceParts.format(now) : now).slice(0, 4);
  /** Whole calendar days from `now`'s day to the instant's, in the browser timezone. */
  const dayOffset = (date: Date, now: Date): number => {
    const noonOf = (value: Date) => {
      const [y, m, d] = browserParts.format(value).split("-").map(Number);
      return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12);
    };
    return Math.round((noonOf(date) - noonOf(now)) / (24 * HOUR));
  };
  const numberWith = new Map<string, Intl.NumberFormat>();
  const percent = new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 });
  const listOf = cached((kind) => new Intl.ListFormat(locale, { type: kind as "conjunction" | "disjunction" }));
  return {
    itemDate(date, style = "medium") {
      if (date instanceof Date) return itemDateIn(`${style} ${workspaceTimeZone}`).format(date);
      return itemDateIn(`${style} UTC`).format(isoDateToInstant(date));
    },
    fullDay(date) {
      return fullDayFormat.format(isoDateToInstant(date));
    },
    timestamp(instant, style = "medium") {
      return timestampIn(style).format(toDate(instant));
    },
    time(instant) {
      return timeOfDay.format(toDate(instant));
    },
    day(instant, now) {
      const date = toDate(instant);
      return dayFrom(date, now, dayOffset(date, now), false);
    },
    timeRange(start, end) {
      const from = toDate(start);
      const to = toDate(end);
      return (browserParts.format(from) === browserParts.format(to) ? timeOfDay : acrossDays).formatRange(from, to);
    },
    when(instant, now, whenOptions) {
      const date = toDate(instant);
      const time = whenOptions?.time === true;
      const ms = date.getTime() - now.getTime();
      const away = Math.abs(ms);
      if (away < MINUTE) return sentenceCase(relativeWords.format(0, "second"), locale);
      const days = dayOffset(date, now);
      if (days === 0) {
        if (time && away >= HOUR) return timeOfDay.format(date);
        const [count, unit] = away < HOUR ? [Math.floor(away / MINUTE), "minute"] : [Math.floor(away / HOUR), "hour"];
        return ms < 0 ? unitNarrow(unit).format(count) : relativeNarrow.format(count, unit as Intl.RelativeTimeFormatUnit);
      }
      return dayFrom(date, now, days, time);
    },
    date(value, now, dateOptions) {
      if (value instanceof Date) {
        const sameYear = now instanceof Date ? sameBrowserYear(value, now) : browserParts.format(value).slice(0, 4) === now.slice(0, 4);
        return monthDayIn(`${sameYear ? "" : "year"} ${dateOptions?.time === true ? "time" : ""} ${browserTimeZone}`).format(value);
      }
      return monthDayIn(`${value.slice(0, 4) === workspaceYear(now) ? "" : "year"}  UTC`).format(isoDateToInstant(value));
    },
    number(value, numberOptions) {
      // The options are a plain record of `Intl.NumberFormatOptions`, so its JSON is its identity.
      const key = numberOptions === undefined ? "" : JSON.stringify(numberOptions);
      let formatter = numberWith.get(key);
      if (formatter === undefined) {
        formatter = new Intl.NumberFormat(locale, numberOptions);
        numberWith.set(key, formatter);
      }
      return formatter.format(value);
    },
    percent(fraction) {
      return percent.format(fraction);
    },
    list(items, kind = "conjunction") {
      return listOf(kind).format(items);
    },
    workspaceDateOf(instant) {
      return workspaceParts.format(instant) as IsoDate;
    },
    browserDateOf(instant) {
      return browserParts.format(toDate(instant)) as IsoDate;
    },
  };
}
